import React, { FunctionComponent, useEffect, useState } from 'react'
import { Button, Col, Modal, Typography, notification, Row, Space } from 'antd'
import { CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { attachPaymentMethods, detachPaymentMethods, getPaymentMethods } from '../../../context/subscription'
import { PaymentMethod } from '../../../context/types'

const { Text } = Typography

interface CardBlockProps {
	card?: any
	hideAddBtn?: boolean
	modalView?: boolean
	showRemoveCardBtn?: boolean
	showCancelCardBtn?: boolean
	onAfterSave?: (paymentMethod: PaymentMethod) => void
	onCancel?: () => void
}

const CardBlock: FunctionComponent<CardBlockProps> = (props) => {
	const { hideAddBtn, modalView, showRemoveCardBtn, showCancelCardBtn, card, onAfterSave } = props

	const stripe = useStripe()
	const elements = useElements()

	const [showAddCard, setShowAddCard] = useState(false)
	const [loading, setLoading] = useState(false)
	const [selectedCard, setSelectedCard] = useState<PaymentMethod>()

	const cardData = selectedCard?.card
	const handleShowAddCard = () => setShowAddCard(true)
	const handleHideAddCard = () => setShowAddCard(false)

	const handleError = (error: any) => {
		notification.error({ message: 'Error', description: error.message || 'Oops! Something went wrong!' })
	}

	useEffect(() => {
		if (card !== undefined) {
			setSelectedCard(card)
		} else {
			setLoading(true)
			getPaymentMethods()
				.then((response) => {
					if (response.paymentMethods?.length) {
						setSelectedCard(response.paymentMethods[0])
					}
				})
				.catch(handleError)
				.finally(() => setLoading(false))
		}
	}, [card])

	const addCard = async () => {
		let attachedPaymentMethod: PaymentMethod = null
		try {
			const cardNumberElement = elements.getElement(CardNumberElement)
			const cardCvcElement = elements.getElement(CardCvcElement)
			const cardExpiryElement = elements.getElement(CardExpiryElement)
			if (cardNumberElement && cardCvcElement && cardExpiryElement) {
				setLoading(true)

				const successMsg = selectedCard ? 'Credit card was changed' : 'Credit card was added'
				const { error, paymentMethod } = await stripe.createPaymentMethod({
					type: 'card',
					card: cardNumberElement,
				})

				if (error) {
					return handleError(error)
				}

				attachedPaymentMethod = await attachPaymentMethods(paymentMethod.id)
				setSelectedCard(attachedPaymentMethod)
				notification.success({ message: 'Success', description: successMsg })
			}
			setShowAddCard(false)
		} catch (error) {
			handleError(error)
		} finally {
			setLoading(false)
		}

		onAfterSave?.(attachedPaymentMethod)
	}

	const detachPaymentMethod = async (id: string) => {
		Modal.confirm({
			title: 'Confirmation',
			content: 'Are you sure you want to remove credit card?',
			onOk: async () => {
				try {
					await detachPaymentMethods(id)
					setSelectedCard(null)
					onAfterSave(null)
					notification.success({ message: 'Success', description: 'Credit card was removed' })
				} catch (error) {
					handleError(error)
				}
			},
		})
	}

	if (cardData && !showAddCard)
		return (
			<div className="card-container">
				<Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
					<Col xs={24} md={12}>
						<Button type="default" shape="round" size="large" className="CardBtn" block>
							<span className="CardBtnTitle">
								{cardData.brand} **** **** **** {cardData.last4}
							</span>
						</Button>
					</Col>
				</Row>
				<Row gutter={[10, 10]}>
					<Col xs={24} md={6}>
						<Space size={10}>
							<Button onClick={handleShowAddCard} type="primary" ghost shape="round" size="large">
								{!!selectedCard ? 'Change card' : 'Add new card'}
							</Button>
							{showRemoveCardBtn && (
								<Button
									onClick={() => detachPaymentMethod(selectedCard.id)}
									type="link"
									danger
									shape="round"
									size="large"
								>
									Remove card
								</Button>
							)}
						</Space>
					</Col>
				</Row>
			</div>
		)

	const showBtns = !hideAddBtn || (!!hideAddBtn && !!cardData)

	return (
		<div className="card-container">
			<Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
				<Col md={modalView ? 12 : 7} xs={24}>
					<div className="card-element">
						<CardNumberElement />
						<Text>Card number</Text>
					</div>
				</Col>
				<Col md={modalView ? 5 : 4} xs={12}>
					<div className="card-element">
						<CardCvcElement options={{ placeholder: '***' }} />
						<Text>CVC</Text>
					</div>
				</Col>
				<Col md={modalView ? 7 : 5} xs={12}>
					<div className="card-element">
						<CardExpiryElement />
						<Text>Expiration Date</Text>
					</div>
				</Col>
			</Row>

			<Row gutter={[10, 10]}>
				{showBtns && (
					<Col xs={24} md={8}>
						<Space size={10}>
							{!hideAddBtn && (
								<Button onClick={addCard} type="primary" ghost shape="round" size="large" loading={loading}>
									Save Card
								</Button>
							)}
							{showCancelCardBtn && (
								<Button
									onClick={handleHideAddCard}
									className="skip-btn"
									type="link"
									ghost
									shape="round"
									size="large"
									style={{ color: '#8C8C8C' }}
								>
									Reset Data
								</Button>
							)}
						</Space>
					</Col>
				)}
			</Row>
		</div>
	)
}

CardBlock.defaultProps = {
	showRemoveCardBtn: false,
	showCancelCardBtn: true,
}

export default CardBlock
